import { Link } from "react-router-dom";
import { Wine, Instagram, Facebook, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setSiteSettings(response.data);
      } catch (error) {
        console.error("Error fetching site settings:", error);
      }
    };
    fetchSettings();
  }, []);

  // Default values if CMS data not loaded
  const footer = siteSettings?.footer || {
    tagline: "Crafting extraordinary mocktail experiences for life's most memorable celebrations.",
    phone: "(780) 555-0123",
    email: "hello@mixandmingle.ca",
    address: "Edmonton & Calgary, Alberta",
    social_links: {}
  };
  
  const quickLinks = siteSettings?.quick_links || [
    { label: "Our Experiences", url: "/experiences" },
    { label: "Treats", url: "/treats" },
    { label: "Upcoming Events", url: "/events" },
    { label: "Gallery", url: "/gallery" },
    { label: "Book Now", url: "/booking" },
    { label: "Contact Us", url: "/contact" }
  ];
  
  const services = siteSettings?.services || [
    "Wedding Receptions",
    "Corporate Events",
    "Private Parties",
    "Birthday Celebrations",
    "Custom Mocktail Design"
  ];
  return (
    <footer className="footer" data-testid="footer">
      <div className="container-luxury">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <Wine className="w-8 h-8 text-[#D4AF37]" />
              <span className="font-['Playfair_Display'] text-xl font-semibold">
                <span className="text-[#F5F5F0]">Mix & Mingle </span>
                <span className="text-[#D4AF37]">Events</span>
              </span>
            </Link>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-6">
              {footer.tagline}
            </p>
            <div className="flex gap-4">
              {footer.social_links?.instagram && (
                <a
                  href={footer.social_links.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-[rgba(212,175,55,0.3)] flex items-center justify-center hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all group"
                  data-testid="social-instagram"
                  aria-label="Instagram">
                  <Instagram className="w-5 h-5 text-[#D4AF37] group-hover:text-[#050505]" />
                </a>
              )}
              {footer.social_links?.facebook && (
                <a
                  href={footer.social_links.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-[rgba(212,175,55,0.3)] flex items-center justify-center hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all group"
                  data-testid="social-facebook"
                  aria-label="Facebook">
                  <Facebook className="w-5 h-5 text-[#D4AF37] group-hover:text-[#050505]" />
                </a>
              )}
              {footer.social_links?.tiktok && (
                <a
                  href={footer.social_links.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-[rgba(212,175,55,0.3)] flex items-center justify-center hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all group"
                  data-testid="social-tiktok"
                  aria-label="TikTok">
                  <svg className="w-5 h-5 text-[#D4AF37] group-hover:text-[#050505]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.65a8.16 8.16 0 0 0 4.77 1.52V6.72a4.85 4.85 0 0 1-1.84 0Z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Quick Links - CMS Editable */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg mb-6 text-[#F5F5F0]">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              {quickLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.url}
                  className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Services - CMS Editable */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg mb-6 text-[#F5F5F0]">Services</h4>
            <nav className="flex flex-col gap-3">
              {services.map((service, index) => (
                <span
                  key={index}
                  className="text-[#A1A1AA] text-sm"
                >
                  {service}
                </span>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg mb-6 text-[#F5F5F0]">Contact</h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p className="text-[#A1A1AA] text-sm">
                  {footer.address}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#D4AF37]" />
                <a
                  href={`tel:${footer.phone?.replace(/[^0-9+]/g, '')}`}
                  className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid="footer-phone">
                  {footer.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#D4AF37]" />
                <a
                  href={`mailto:${footer.email}`}
                  className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid="footer-email">
                  {footer.email}
                </a>
              </div>
              {footer.whatsapp && (
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-[#D4AF37]" />
                  <a
                    href={`https://wa.me/${String(footer.whatsapp).replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors text-sm"
                    data-testid="footer-whatsapp">
                    Chat on WhatsApp
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar - CMS Editable */}
        <div className="border-t border-[rgba(212,175,55,0.15)] pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#52525B] text-sm">
              {footer.copyright || `© ${new Date().getFullYear()} MIX & MINGLE Events. All rights reserved.`}
            </p>
            <div className="flex gap-6">
              {(footer.policy_links || [
                { label: "Privacy Policy", url: "/privacy" },
                { label: "Terms of Service", url: "/terms" }
              ]).map((link, index) => (
                <Link
                  key={index}
                  to={link.url}
                  className="text-[#52525B] hover:text-[#D4AF37] text-sm transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>);

};

export default Footer;